import type { FC } from "react";
import { useEffect, useState } from "react";
import { minAbbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { colorsPalettes, rgba } from "@similarweb/styles";
import styled from "styled-components";
import {
    CenteredFlexRow,
    FlexColumn,
    FlexRow,
    RightFlexRow,
} from "pro-features/styled components/StyledFlex/src/StyledFlex";
import Chart from "pro-features/components/Chart/src/Chart";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noMarginConfig from "pro-features/components/Chart/src/configs/margin/noMarginConfig";
import _ from "lodash";
import { Legends } from "app/components/React/Legends/Legends";
import ReactDOMServer from "react-dom/server";
import { ClosableItemColorMarker } from "app/components/compare/StyledComponent";
import type { IDataType } from "app/components/React/GraphTypeSwitcher/GraphTypeSwitcher";
import { LegendWithOneLineCheckboxFlex } from "@similarweb/ui-components";
import { PERCENTS } from "app/pages/website-analysis/traffic-sources/display-ads/common/DisplayAdsTypes";
import { CHART_COLORS } from "app/constants/ChartColors";
import { EmptyState } from "app/pages/website-analysis/traffic-sources/display-ads/common/EmptyState";
import { chosenSites as chosenSitesService } from "app/common/services/chosenSites";
import type { ILegendPropsWithValue } from "@similarweb/ui-components";
import { allTrackers } from "app/services/track/track";

export type PieChartDataItemType = {
    color?: string;
    name: string;
    percent: number;
    y: number;
    visible?: boolean;
};

type PieChartConfigDataItemType = {
    chartType: IDataType;
    tooltipHeaders: string[];
    tooltipContainerWidth?: number;
    legendsMinWidth?: number;
    legendsMaxWidth?: number;
    isEmptyStateSmall?: boolean;
    legendComponent?: FC<ILegendPropsWithValue>;
    isLegendToggleDisabled?: boolean;
};

type LegendType = {
    name: string;
    color: string;
    visible: boolean;
    isDisabled: boolean;
    data: object;
};

interface IPieChartProps {
    pieChartData: PieChartDataItemType[];
    pieChartConfig: PieChartConfigDataItemType;
}

const PieLegend = styled(FlexColumn)<{
    itemsCount: number;
    legendsMinWidth: number;
    legendsMaxWidth: number;
}>`
    min-width: ${({ legendsMinWidth }) => legendsMinWidth}px;
    max-width: ${({ legendsMaxWidth }) => legendsMaxWidth}px;
    min-height: ${({ itemsCount }) => itemsCount * 30}px;
    justify-content: center;
    margin-right: 24px;
    flex: 1;
`;

const PieChartContainer = styled(CenteredFlexRow)`
    flex: 1;
`;

const PieChartWrapper = styled(CenteredFlexRow)`
    justify-content: space-between;
    width: 100%;
`;

const StyledLegendWrapper = styled.span`
    width: 100%;
    margin-bottom: 8px;

    .winnerIcon {
        padding-top: 0;
    }
`;

const TableCellStyle = styled(RightFlexRow)`
    width: 90px;
    padding: 4px 16px 4px 16px;

    &:nth-child(1) {
        width: 100%;
        justify-content: flex-start;
    }

    &:last-child {
        border-right: none;
    }
`;

const TableHeadersStyle = styled(FlexRow)`
    font-size: 12px;
    font-style: normal;
    line-height: 16px;
    font-weight: 400;
    opacity: 0.8;
`;

const TableRowStyle = styled(FlexRow)`
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    letter-spacing: 0.6px;
    line-height: 24px;
`;

const TableCellDomainMarker = styled(ClosableItemColorMarker)<{ background: string }>`
    position: static;
    background-color: ${(p) => p.background};
    margin: 0 8px 0 0;
    flex-shrink: 0;
    width: 10px;
    height: 10px;
`;

const TableCellDomainName = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const TooltipContainer = styled.div<{ tooltipContainerWidth: number }>`
    ${({ tooltipContainerWidth }) => `width:${tooltipContainerWidth}px`}
    padding: 16px 0;
    background-color: ${colorsPalettes.carbon[0]};
    box-shadow: 0 3px 6px 0 ${rgba(colorsPalettes.carbon[200], 0.5)};
    border-radius: 5px;
    margin: 12px 0 8px 0;
    color: ${rgba(colorsPalettes.carbon[500], 0.8)};
`;

export const PieChart: FC<IPieChartProps> = ({ pieChartData, pieChartConfig }) => {
    const {
        chartType,
        tooltipHeaders,
        tooltipContainerWidth = 233,
        legendsMinWidth = 188,
        legendsMaxWidth = 250,
        legendComponent = LegendWithOneLineCheckboxFlex,
        isLegendToggleDisabled = false,
        isEmptyStateSmall = false,
    } = pieChartConfig;

    const [legendItems, setLegendItems] = useState<LegendType[]>([]);
    const [isChartHidden, setIsChartHidden] = useState<boolean>(false);
    const getSiteColor = chosenSitesService.getSiteColor;

    const initRawData = (data) => {
        return data.map((item) => {
            return {
                ...item,
                visible: true, // the charts needs this for hide / show
            };
        });
    };
    const [chartData, setChartData] = useState<PieChartDataItemType[]>(initRawData(pieChartData));

    const transformRawDataToLegends = (data) => {
        const getWinner = () => {
            if (data.length > 0) {
                return _.maxBy(data, (item: PieChartDataItemType) => {
                    return chartType.value === PERCENTS ? item.percent : item.y;
                });
            } else {
                return "";
            }
        };
        const transformedLegendsData = data.reduce((acc, pointObject) => {
            pointObject.color =
                pointObject.name === "Others"
                    ? CHART_COLORS.chartMainColors[CHART_COLORS.chartMainColors.length - 1]
                    : undefined;
            const currentLegendValue = formattedPoint(pointObject);
            acc.push({
                name: pointObject.name,
                color: pointObject.color ? pointObject.color : getSiteColor(pointObject.name),
                visible: currentLegendValue !== "N/A",
                isDisabled: currentLegendValue === "N/A",
                data: currentLegendValue,
                get isWinner() {
                    return acc.length > 1 && pointObject.name === getWinner().toString();
                },
            });
            return acc;
        }, []);
        checkIsChartHidden(transformedLegendsData);
        return transformedLegendsData;
    };

    useEffect(() => {
        const data = initRawData(pieChartData);
        setChartData(data);
        setLegendItems(transformRawDataToLegends(data));
    }, [chartType, pieChartData]);

    const formattedPoint = (point) => {
        const numberFormatter = (value) => minAbbrNumberFilter()(value);
        const percentageFormatter = (value) => percentageSignFilter()(value, 2);
        const rawValue = chartType.value === PERCENTS ? point.percent : point.y;
        const formattedValue = (rawValue) =>
            chartType.value === PERCENTS ? percentageFormatter(rawValue) : numberFormatter(rawValue);
        return rawValue && rawValue > 0 ? formattedValue(rawValue) : "N/A";
    };

    const transformToPieChartObject = (data) => [
        {
            data: data,
            showInLegend: true,
            type: "pie",
        },
    ];

    const getPieChartTooltip = ({ point }) => {
        const currentPointValue = formattedPoint(point);
        const TableHeaders = () => {
            const headerItems = tooltipHeaders.map((header) => <TableCellStyle key={header}>{header}</TableCellStyle>);
            return <TableHeadersStyle> {headerItems} </TableHeadersStyle>;
        };

        return ReactDOMServer.renderToString(
            <TooltipContainer tooltipContainerWidth={tooltipContainerWidth}>
                <TableHeaders />
                <TableRowStyle>
                    <TableCellStyle>
                        <TableCellDomainMarker background={point.color} />
                        <TableCellDomainName>{point.name}</TableCellDomainName>
                    </TableCellStyle>
                    <TableCellStyle>{currentPointValue}</TableCellStyle>
                </TableRowStyle>
            </TooltipContainer>,
        );
    };

    const getPieChartConfig = () => {
        return combineConfigs({}, [
            noMarginConfig,
            {
                colors: CHART_COLORS.chartMainColors,
                chart: {
                    type: "pie",
                    plotBackgroundColor: "transparent",
                    events: {},
                },
                plotOptions: {
                    pie: {
                        innerSize: "60%",
                        dataLabels: {
                            enabled: false,
                        },
                    },
                },
                legend: {
                    enabled: false,
                },
                tooltip: {
                    followPointer: false,
                    shared: true,
                    useHTML: true,
                    outside: true,
                    backgroundColor: undefined,
                    borderWidth: 0,
                    formatter: function () {
                        return getPieChartTooltip({
                            point: this.point,
                        });
                    },
                },
            },
        ]);
    };

    const checkIsChartHidden = (legends) => {
        const totalDisabledLegends = legends.reduce((total, item) => {
            if (item.isDisabled || !item.visible) {
                total += 1;
            }
            return total;
        }, 0);
        setIsChartHidden(totalDisabledLegends === legends.length);
    };

    const toggleVisibleProp = ({ data, legendName }) => {
        return data.map((item) => {
            return {
                ...item,
                visible: item.name === legendName ? !item.visible : item.visible,
            };
        });
    };

    const toggleSeries = (legend) => {
        const { name, visible } = legend;
        const action = visible ? "add" : "remove";
        allTrackers.dispatchTrackEvent(
            "Graph Filter",
            "click",
            `Pie Chart/display_and_video_engagement/${name || ""}/${action || ""}/`,
        );
        const updatedChartData = toggleVisibleProp({ data: chartData, legendName: name });
        setChartData(updatedChartData);
        const updatedLegendItems = toggleVisibleProp({ data: legendItems, legendName: name });
        checkIsChartHidden(updatedLegendItems);
        setLegendItems(updatedLegendItems);
    };

    return (
        <PieChartContainer>
            <PieChartWrapper>
                {isChartHidden ? (
                    <EmptyState
                        titleKey="display.ads.overview.empty.state.title"
                        isSmallWidget={isEmptyStateSmall}
                    />
                ) : (
                    <Chart
                        type={"pie"}
                        config={getPieChartConfig()}
                        data={transformToPieChartObject(chartData)}
                        domProps={{ style: { height: "160px", padding: "24px", flex: "1" } }}
                    />
                )}
                <PieLegend
                    data-automation="pie-chart-legends"
                    itemsCount={chartData.length}
                    legendsMinWidth={legendsMinWidth}
                    legendsMaxWidth={legendsMaxWidth}
                >
                    <Legends
                        legendComponent={legendComponent}
                        legendComponentWrapper={StyledLegendWrapper}
                        legendItems={legendItems}
                        toggleSeries={!isLegendToggleDisabled && toggleSeries}
                        gridDirection="column"
                        textMaxWidth={
                            window.innerWidth < 1680 ? (window.innerWidth > 1366 ? "125px" : "100px") : "150px"
                        }
                    />
                </PieLegend>
            </PieChartWrapper>
        </PieChartContainer>
    );
};
